"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, HandHeart, ShieldCheck, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";

const team = [
  {
    photo: "/header/2.webp",
    name: "Paula Pricope",
    role: "Cofondatoare · Psihoterapeut",
    bio: "Construiesc echipa de psihoterapeuți și mă asigur că fiecare proces rămâne etic și empatic, de la primul contact până la ultimul.",
    bg: "#2C969B", // teal
  },
  {
    photo: "/header/3.webp",
    name: "Eduard Modreanu",
    role: "Cofondator · Software Engineer",
    bio: "Construiesc platforma care leagă toate piesele. Discret, dar mereu acolo: chat, programări, plăți, recomandări.",
    bg: "#7C5A2B", // warm brown
  },
  {
    photo: "/header/1.webp",
    // Head starts at ~38% of this source, so a centred crop leaves a band of
    // empty background above her that no other portrait has.
    position: "object-bottom",
    name: "Bianca Bejenariu",
    role: "Marketing Specialist",
    bio: "Duc ceea ce facem la oamenii care au nevoie să afle. Cu cuvinte calde, nu cu hype.",
    bg: "#DC7050", // orange
  },
  {
    photo: "/header/4.webp",
    name: "Anastasia Vieru",
    role: "Graphic Designer",
    bio: "Dau formă lucrurilor: culorile, fonturile, fiecare detaliu care face Mindtale să se simtă cald și de încredere.",
    bg: "#2C969B", // teal
  },
  {
    photo: "/header/5.webp",
    name: "Alexandru Tudor",
    role: "Software Engineer",
    bio: "Scriu codul din spate, ca totul să meargă lin: de la programări la plăți, fără bătăi de cap pentru tine.",
    bg: "#7C5A2B", // warm brown
  },
  {
    photo: "/header/6.webp",
    name: "Talida Caraman",
    role: "Software Engineer",
    bio: "Construiesc și testez ce folosești zi de zi, ca experiența să rămână simplă și sigură de la primul click.",
    bg: "#DC7050", // orange
  },
];

const pillars = [
  {
    label: "01",
    title: "Platformă gândită cu psihoterapeuții",
    text: "Online, sigur, ușor de folosit. Fără friction inutilă. Terapeuții setează prețurile, programul, abordarea — noi construim infrastructura.",
  },
  {
    label: "02",
    title: "Fondul de Terapie Mindtale",
    text: "10% din fiecare ședință plătită merg într-un fond transparent. Susține procese terapeutice complete pentru cineva care nu și-ar permite.",
  },
  {
    label: "03",
    title: "Cazuri reale, urmărite real",
    text: "Beneficiarii sunt propuși de ONG-uri și parteneri, validați cu criterii clare, susținuți cu pachete reale — nu o singură ședință simbolică.",
  },
];

const values = [
  {
    pill: "Model social integrat",
    title: "Acces real, nu doar promisiune",
    text: "Componenta socială nu e atașată la final ca „CSR”. E în felul în care funcționează platforma de la primul lei plătit.",
  },
  {
    pill: "Fără expunere, fără stigmat",
    title: "Demnitate și confidențialitate",
    text: "Cei care intră în programul social vin prin canale sigure și parteneri de încredere. Zero expunere publică. Zero etichetare.",
  },
  {
    pill: "Cu și pentru comunitate",
    title: "Parteneriate, nu soluții izolate",
    text: "Lucrăm cu ONG-uri, specialiști și comunități locale. Construim intervenții sustenabile, nu ajutor punctual.",
  },
];

export default function AboutPage() {
  return (
    <section className="relative bg-[#FDF9F5] text-[#264653]">

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-4xl px-6 pt-20 pb-12 text-center lg:px-8 lg:pt-24">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#2C969B]/20 bg-white/70 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0F6B70] backdrop-blur">
          <Sparkles className="size-3.5" />
          Despre Mindtale
        </div>

        <h1 className="text-3xl font-bold leading-[1.1] tracking-tight text-[#264653] sm:text-4xl lg:text-[3rem]">
          Suntem șase oameni care cred că{" "}
          <span className="text-[#2C969B]">terapia merită să fie aproape</span>.
        </h1>

        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#264653]/80 sm:text-lg">
          Am construit Mindtale ca un pod: între oameni care își permit să caute
          ajutor, oameni care nu își permit încă, și terapeuți care vor să aibă
          impact real, nu doar ore facturate.
        </p>
      </div>

      {/* ── Team grid ────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-6xl px-6 pb-16 lg:px-8 lg:pb-20">
        <div className="mb-8 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2C969B]">
            Echipa
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-[#264653] sm:text-3xl">
            Cine este în spatele Mindtale
          </h2>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {team.map(({ photo, name, role, bio, bg, position }) => (
            <div
              key={photo}
              className="group overflow-hidden rounded-3xl bg-white shadow-[0_4px_24px_rgba(38,70,83,0.05)] ring-1 ring-[#264653]/8 transition hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(38,70,83,0.10)]"
            >
              <div
                className="relative aspect-[3/4] w-full overflow-hidden"
                style={{ backgroundColor: bg }}
              >
                <Image
                  src={photo}
                  alt={name}
                  fill
                  sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                  className={`object-cover ${position ?? ""} transition duration-500 group-hover:scale-[1.02]`}
                />
              </div>
              <div className="p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#0F6B70]">
                  {role}
                </p>
                <h3 className="mt-1 text-[18px] font-semibold text-[#264653]">
                  {name}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-[#264653]/70">
                  {bio}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── De ce Mindtale ───────────────────────────────────────────── */}
      <div className="border-y border-[#2C969B]/10 bg-white/90">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 lg:grid-cols-2 lg:items-center lg:px-8 lg:py-20">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2C969B]">
              De ce existăm
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-[#264653] sm:text-3xl">
              Cei care au cea mai mare nevoie ajung cel mai greu la ajutor.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#264653]/80">
              În România, persoanele cu venituri reduse, tinerii fără sprijin,
              părinții copleșiți, oamenii din comunități marginalizate sau din
              sate fără cabinete — toți au nevoie de psihoterapie. Puțini ajung
              la una.
            </p>
            <p className="mt-3 text-base leading-relaxed text-[#264653]/80">
              În același timp, psihoterapeuții independenți caută un spațiu
              sigur și etic ca să lucreze online, fără presiune comercială
              agresivă.
            </p>
          </div>

          <div className="grid gap-3">
            <div className="flex items-start gap-3 rounded-2xl bg-[#FDF9F5] p-4 ring-1 ring-[#2C969B]/10">
              <ShieldCheck className="mt-0.5 size-5 shrink-0 text-[#2C969B]" />
              <div>
                <p className="text-sm font-semibold text-[#264653]">
                  Psihoterapie accesibilă și demnă
                </p>
                <p className="mt-1 text-sm text-[#264653]/75">
                  Pentru oameni care altfel nu și-ar permite.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-2xl bg-[#FDF9F5] p-4 ring-1 ring-[#2C969B]/10">
              <HandHeart className="mt-0.5 size-5 shrink-0 text-[#DC7050]" />
              <div>
                <p className="text-sm font-semibold text-[#264653]">
                  Conectăm oameni cu psihoterapeuți acreditați
                </p>
                <p className="mt-1 text-sm text-[#264653]/75">
                  Match direct cu echipa noastră COPSI.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-2xl bg-[#FDF9F5] p-4 ring-1 ring-[#2C969B]/10">
              <Sparkles className="mt-0.5 size-5 shrink-0 text-[#0F6B70]" />
              <div>
                <p className="text-sm font-semibold text-[#264653]">
                  Reducem barierele geografice și de timp
                </p>
                <p className="mt-1 text-sm text-[#264653]/75">
                  Online, în limba ta, la ora ta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cum funcționează ─────────────────────────────────────────── */}
      <div className="bg-[#FDF9F5] py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2C969B]">
              Cum funcționează
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-[#264653] sm:text-3xl">
              Comercial și social, în același mecanism.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[#264653]/75 sm:text-base">
              Nu donație ocazională, nu CSR la sfârșit de an. Modelul nostru
              integrează ajutorul social în chiar felul în care funcționează
              platforma.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {pillars.map(({ label, title, text }) => (
              <div
                key={title}
                className="relative rounded-3xl bg-white p-6 shadow-[0_4px_24px_rgba(38,70,83,0.04)] ring-1 ring-[#264653]/8 transition hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(38,70,83,0.08)]"
              >
                <span
                  className="absolute right-5 top-5 text-[11px] font-semibold tracking-widest text-[#2C969B]"
                >
                  {label}
                </span>
                <h3 className="text-[17px] font-semibold text-[#264653] sm:text-[18px]">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-[#264653]/70">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Valori ───────────────────────────────────────────────────── */}
      <div className="border-y border-[#2C969B]/10 bg-white/90 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2C969B]">
              Valori
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-[#264653] sm:text-3xl">
              Cum luăm fiecare decizie
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {values.map(({ pill, title, text }) => (
              <div
                key={title}
                className="rounded-3xl bg-[#FDF9F5] p-5 ring-1 ring-[#264653]/8 transition hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(38,70,83,0.06)]"
              >
                <div className="mb-2 inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#0F6B70] ring-1 ring-[#2C969B]/15">
                  {pill}
                </div>
                <h3 className="text-[16px] font-semibold text-[#264653]">
                  {title}
                </h3>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-[#264653]/75">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA final ────────────────────────────────────────────────── */}
      <div className="bg-[#FDF9F5] py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-2xl font-semibold text-[#264653] sm:text-3xl">
            Vrei să construim asta împreună?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#264653]/80 sm:text-base">
            Psihoterapeut, ONG, companie sau pur și simplu cineva care crede în
            idee — hai să transformăm modelul în impact constant.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link href="/contact">
              <Button
                className="rounded-full bg-[#2C969B] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(44,150,155,0.30)] hover:bg-[#22797e]"
              >
                Scrie-ne pentru parteneriat
                <ArrowRight className="ml-1.5 size-4" />
              </Button>
            </Link>
            <Link href="/colaborare/terapeuti">
              <Button
                variant="outline"
                className="rounded-full border-[#264653]/20 px-6 py-2.5 text-sm font-medium text-[#264653] hover:bg-[#264653]/5"
              >
                Sunt psihoterapeut
              </Button>
            </Link>
          </div>
        </div>
      </div>

    </section>
  );
}
